import { CommonModule } from '@angular/common';
import {
    HttpBackend,
    HttpClientModule,
    HttpHandler,
    HttpInterceptor,
    HTTP_INTERCEPTORS
} from '@angular/common/http';
import {
    COMPILER_OPTIONS,
    ErrorHandler,
    Injector,
    ModuleWithProviders,
    NgModule
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ChartsModule } from 'angular-bootstrap-md';
import Amplify, { Auth } from 'aws-amplify';
import { AWS_AMPLIFY, AWS_AMPLIFY_AUTH, ISettings } from '@lsq-frontend/angular-shared/settings';
import { LsqFilePickerComponent } from './components/forms/file-picker/lsq-file-picker.component';
import { LsqSearchComponent } from './components/forms/search/lsq-search.component';
import { LsqBackendComponent } from './components/pages/backend/lsq-backend.component';
import { LsqClaimsComponent } from './components/pages/claims/lsq-claims.component';
import { LsqHealthComponent } from './components/pages/health/lsq-health.component';
import { LsqLegalDocumentComponent } from './components/pages/legal-document/legal-document.component';
import { BankAccountCardComponent } from './components/ui-elements/bank-account-card/bank-account-card.component';
import { LsqBannerSearchComponent } from './components/ui-elements/banner-search/lsq-banner-search.component';
import { LsqBannerStepTabsComponent } from './components/ui-elements/banner-step-tabs/lsq-banner-step-tabs.component';
import { LsqBannerTabsComponent } from './components/ui-elements/banner-tabs/lsq-banner-tabs.component';
import { LsqBannerComponent } from './components/ui-elements/banner/lsq-banner.component';
import { LsqCardTabsComponent } from './components/ui-elements/card-tabs/lsq-card-tabs.component';
import { CardComponent } from './components/ui-elements/card/card.component';
import { LsqChartComponent } from './components/ui-elements/chart/lsq-chart.component';
import { LsqComingSoonBoxComponent } from './components/ui-elements/coming-soon-box/lsq-coming-soon-box.component';
import { DataCardComponent } from './components/ui-elements/data-card/data-card.component';
import {
    LsqBarDataPoint,
    LsqDataPointBarComponent
} from './components/ui-elements/data-point-bar/lsq-data-point-bar.component';
import { IDateRange } from './components/ui-elements/date-picker/models/date-range';
import {
    LsqDetailedTimelineComponent,
    TimelineDetailItem,
    TimelineItem
} from './components/ui-elements/detailed-timeline/lsq-detailed-timeline.component';
import { LsqExportButtonComponent } from './components/ui-elements/export-button/lsq-export-button.component';
import { LsqExportFormComponent } from './components/ui-elements/export-form/lsq-export-form.component';
import { FaqItemComponent } from './components/ui-elements/faq-item/faq-item.component';
import { LsqFeedbackFormComponent } from './components/ui-elements/feedback-form/lsq-feedback-form.component';
import { LsqIneligibleTagComponent } from './components/ui-elements/ineligible-tag/lsq-ineligible-tag.component';
import { InfoBoxComponent } from './components/ui-elements/info-box/info-box.component';
import { LsqInfographicBoxComponent } from './components/ui-elements/infographic-box/lsq-infographic-box.component';
import { LsqMobileBannerTabsComponent } from './components/ui-elements/mobile-banner-tabs/lsq-mobile-banner-tabs.component';
import { PageHeaderComponent } from './components/ui-elements/page-header/page-header.component';
import { LsqProgressBarComponent } from './components/ui-elements/progress-bar/lsq-progress-bar.component';
import { LsqReportActionsLayoutComponent } from './components/ui-elements/report-actions-layout/lsq-report-actions-layout.component';
import { ScfVendorAgreementTextComponent } from './components/ui-elements/scf-vendor-agreement-text/scf-vendor-agreement-text.component';
import { LsqSidebarComponent } from './components/ui-elements/sidebar/sidebar.component';
import { LsqStickyWrapperComponent } from './components/ui-elements/sticky-wrapper/lsq-sticky-wrapper.component';
import { TabsComponent } from './components/ui-elements/tabs/tabs.component';
import {
    ITimelineEvent,
    LsqTimelineComponent
} from './components/ui-elements/timeline/lsq-timeline.component';
import { LsqToggleDropdownComponent } from './components/ui-elements/toggle-dropdown/lsq-toggle-dropdown.component';
import { LsqTooltipComponent } from './components/ui-elements/tooltip/lsq-tooltip.component';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from './constants/authentication.constants';
import {
    ATLAS,
    FORGOT_PASSWORD_SERVICE,
    SEND_PHONE_NUMBER_VERIFICATION_CODE_SERVICE,
    VALIDATE_PHONE_NUMBER_VERIFICATION_CODE_SERVICE
} from './constants/injector.constants';
import { LsqCoreModule } from './core';
import { LsqHasTooltipDirective } from './directives/lsq-has-tooltip.directive';
import { LsqFormsModule } from './forms';
import { AdminGuard } from './routing/guards/admin/admin.guard';
import { AuthorizationGuard } from './routing/guards/authorization/authorization.guard';
import { NotAuthorizedGuard } from './routing/guards/authorization/not-authorized.guard';
import { ClaimGuard } from './routing/guards/claim/claim.guard';
import { PayLaterGuard } from './routing/guards/pay-later/pay-later.guard';
import { EnvironmentsGuard } from './routing/guards/environments/environments.guard';
import { FeatureFlagGuard } from './routing/guards/feature-flag/feature-flag.guard';
import { InterceptorGuard } from './routing/guards/interceptor/interceptor.guard';
import { WorkflowMonitorGuard } from './routing/guards/workflow-monitor/workflow-monitor.guard';
import { DownloadService } from './services/api/download.service';
import { FilterService } from './services/filter/filter.service';
import { HttpInterceptorHandler } from './services/interceptors/http-interceptor.handler';
import { RequestInterceptor } from './services/interceptors/request.interceptor';
import { ResponseInterceptor } from './services/interceptors/response.interceptor';
import { SearchToggleService } from './services/search-toggle.service';
import { SidebarService } from './services/sidebar/sidebar.service';
import { AuthenticationService } from './services/user/authentication.service';
import { WindowDimensions } from './services/window-reference/window-reference.service';
import { LsqBasePaginationComponent } from './table/components/base-pagination/lsq-base-pagination.component';
import { BaseFilterComponent } from './table/components/filters/base-filter.component';
import { ColumnType } from './table/constants/column-type';
import { TableAppearance } from './table/models/table-appearance';
import { ITableColumn } from './table/models/table-column';
import { ITableRowErrorMessageConfig } from './table/models/table-row-error-message-config';
import { TourHelperService } from './utilities/tour.helper.service';
import { GlobalErrorHandler } from './global-error-handler/global-error.handler';

// Components
// Shared entities and constants
export * from './components/ui-elements/date-picker/constants/date-picker-type';
export * from './constants/action-sheets.constants';
export * from './constants/announcement-type';
export * from './constants/calendar.constants';
export * from './constants/charts.constants';
export * from './constants/class-names.constants';
export * from './constants/color.constants';
export * from './constants/content-types.constants';
export * from './constants/countries.constants';
export * from './constants/currency.constants';
export * from './constants/date-format.constants';
export * from './constants/dates.constants';
export * from './constants/dom-events.constants';
export * from './constants/error-message.constants';
export * from './constants/infographic-box.constants';
export * from './constants/injector.constants';
export * from './constants/input-types.constants';
export * from './constants/interaction.constants';
export * from './constants/keycodes.constants';
export * from './constants/local-storage-keys.constants';
export * from './constants/mask.constants';
export * from './constants/messages.constants';
export * from './constants/navigation.constants';
export * from './constants/orientation.constants';
export * from './constants/regex.constants';
export * from './constants/rpc.constants';
export * from './constants/size.constants';
export * from './constants/states.constants';
export * from './constants/status.constants';
export * from './constants/styling.constants';
export * from './constants/table.constants';
export * from './i18n/i18n-noop';
export * from './models/common';
export * from './models/enums';
export * from './routing/atlas/atlas';
export * from './routing/atlas/atlas.helpers';
export * from './utilities/files.helper';
export * from './utilities/memoize';
export * from './utilities/route.helpers';
export * from './utilities/rxjs.helpers';
export * from './utilities/string.helper';
export * from './utilities/table.helpers';
export * from './utilities/utilities.helper';
export * from './global-error-handler/global-error.handler';

export function errorHandlerFactory(settings: ISettings, injector: Injector): ErrorHandler {
    return settings.bugsnagApiKey ? new GlobalErrorHandler(settings, injector) : new ErrorHandler();
}

export {
    BaseFilterComponent,
    LsqBackendComponent,
    BankAccountCardComponent,
    LsqBannerComponent,
    PageHeaderComponent,
    LsqBannerSearchComponent,
    LsqBannerStepTabsComponent,
    LsqBannerTabsComponent,
    LsqBasePaginationComponent,
    FaqItemComponent,
    LsqCardTabsComponent,
    LsqChartComponent,
    LsqClaimsComponent,
    LsqComingSoonBoxComponent,
    LsqExportButtonComponent,
    LsqSearchComponent,
    LsqDetailedTimelineComponent,
    LsqDataPointBarComponent,
    LsqExportFormComponent,
    LsqFeedbackFormComponent,
    LsqHealthComponent,
    LsqIneligibleTagComponent,
    LsqInfographicBoxComponent,
    LsqMobileBannerTabsComponent,
    LsqProgressBarComponent,
    LsqFilePickerComponent,
    LsqReportActionsLayoutComponent,
    LsqSidebarComponent,
    LsqStickyWrapperComponent,
    LsqTimelineComponent,
    LsqToggleDropdownComponent,
    LsqTooltipComponent,
    CardComponent,
    TabsComponent,
    DataCardComponent,
    LsqLegalDocumentComponent,
    InfoBoxComponent,
    ScfVendorAgreementTextComponent
};

// Directives
export { LsqHasTooltipDirective };
// Services
export {
    AuthenticationService,
    DownloadService,
    FilterService,
    SearchToggleService,
    SidebarService,
    TourHelperService
};
// Routing
export {
    AdminGuard,
    AuthorizationGuard,
    NotAuthorizedGuard,
    ClaimGuard,
    PayLaterGuard,
    EnvironmentsGuard,
    FeatureFlagGuard,
    InterceptorGuard,
    WorkflowMonitorGuard
};
// Models and enums (non-barrel exported)
export {
    ColumnType,
    IDateRange,
    ITableColumn,
    TableAppearance,
    ITableRowErrorMessageConfig,
    ITimelineEvent,
    TimelineDetailItem,
    LsqBarDataPoint,
    TimelineItem,
    WindowDimensions,
    SUPPORT_EMAIL,
    SUPPORT_PHONE
};

export function sidebarServiceFactory(): SidebarService {
    return new SidebarService();
}

/**
 * Adapted from {@link https://github.com/angular/angular/blob/72c7b6edea39f6591463c2a22eb24d087fd756b7/packages/common/http/src/module.ts @angular/common/http}
 *
 * Constructs an `HttpHandler` that applies our `RequestInterceptor` and `ResponseInterceptor` to requests and responses.
 *
 * NOTE: If you want to add another http interceptor to this module, you must use this pattern in
 * order for the library to remain AOT-compatible.
 */
export function interceptingHandler(
    backend: HttpBackend,
    interceptors: HttpInterceptor[]
): HttpHandler {
    if (!interceptors) {
        return backend;
    }
    return interceptors.reduceRight(
        (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
        backend
    );
}

@NgModule({
    imports: [
        LsqFormsModule,
        LsqCoreModule.forChild(),
        // Angular modules
        CommonModule,
        HttpClientModule,
        RouterModule,
        // Feature modules
        ChartsModule,
        RouterModule.forChild([])
    ],
    declarations: [
        // Components
        LsqBackendComponent,
        BankAccountCardComponent,
        LsqBannerComponent,
        PageHeaderComponent,
        LsqBannerSearchComponent,
        LsqBannerStepTabsComponent,
        LsqBannerTabsComponent,
        CardComponent,
        TabsComponent,
        DataCardComponent,
        LsqLegalDocumentComponent,
        FaqItemComponent,
        LsqCardTabsComponent,
        LsqChartComponent,
        LsqClaimsComponent,
        LsqComingSoonBoxComponent,
        LsqExportButtonComponent,
        LsqSearchComponent,
        LsqDetailedTimelineComponent,
        LsqDataPointBarComponent,
        LsqExportFormComponent,
        LsqFeedbackFormComponent,
        LsqHealthComponent,
        LsqIneligibleTagComponent,
        LsqInfographicBoxComponent,
        LsqMobileBannerTabsComponent,
        LsqProgressBarComponent,
        LsqFilePickerComponent,
        LsqReportActionsLayoutComponent,
        LsqSidebarComponent,
        LsqStickyWrapperComponent,
        LsqTimelineComponent,
        LsqToggleDropdownComponent,
        LsqTooltipComponent,
        ScfVendorAgreementTextComponent,
        // Directives
        LsqHasTooltipDirective,
        InfoBoxComponent
    ],
    exports: [
        // LSQ modules
        LsqFormsModule,
        LsqCoreModule,
        // Modules
        ChartsModule,
        CommonModule,
        RouterModule,
        // Components
        LsqBackendComponent,
        BankAccountCardComponent,
        LsqBannerComponent,
        PageHeaderComponent,
        LsqBannerSearchComponent,
        LsqBannerStepTabsComponent,
        LsqBannerTabsComponent,
        CardComponent,
        TabsComponent,
        DataCardComponent,
        LsqLegalDocumentComponent,
        FaqItemComponent,
        LsqCardTabsComponent,
        LsqChartComponent,
        LsqClaimsComponent,
        LsqComingSoonBoxComponent,
        LsqExportButtonComponent,
        LsqSearchComponent,
        LsqDetailedTimelineComponent,
        LsqDataPointBarComponent,
        LsqExportFormComponent,
        LsqFeedbackFormComponent,
        LsqHealthComponent,
        LsqIneligibleTagComponent,
        LsqInfographicBoxComponent,
        LsqMobileBannerTabsComponent,
        LsqProgressBarComponent,
        LsqFilePickerComponent,
        LsqReportActionsLayoutComponent,
        LsqSidebarComponent,
        LsqStickyWrapperComponent,
        LsqTimelineComponent,
        LsqToggleDropdownComponent,
        LsqTooltipComponent,
        ScfVendorAgreementTextComponent,
        // Directives
        LsqHasTooltipDirective,
        InfoBoxComponent,
        LsqHasTooltipDirective
    ],
    providers: [
        { provide: AWS_AMPLIFY, useValue: Amplify },
        { provide: AWS_AMPLIFY_AUTH, useValue: Auth }
    ]
})
export class LsqCommonModule {
    public static forRoot(): ModuleWithProviders<LsqCommonModule> {
        console.debug('Loading libs/LsqCommonModule.forRoot.');
        return {
            ngModule: LsqCommonModule,
            providers: [
                AdminGuard,
                AuthorizationGuard,
                NotAuthorizedGuard,
                ClaimGuard,
                PayLaterGuard,
                EnvironmentsGuard,
                FeatureFlagGuard,
                FilterService,
                InterceptorGuard,
                SearchToggleService,
                TourHelperService,
                WorkflowMonitorGuard,
                {
                    provide: SidebarService,
                    useFactory: sidebarServiceFactory
                },
                {
                    provide: COMPILER_OPTIONS,
                    useValue: {},
                    multi: true
                },
                {
                    provide: FORGOT_PASSWORD_SERVICE,
                    useValue: null
                },
                {
                    provide: SEND_PHONE_NUMBER_VERIFICATION_CODE_SERVICE,
                    useValue: null
                },
                {
                    provide: VALIDATE_PHONE_NUMBER_VERIFICATION_CODE_SERVICE,
                    useValue: null
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: RequestInterceptor,
                    multi: true
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: ResponseInterceptor,
                    multi: true
                },
                {
                    provide: HttpHandler,
                    useFactory: interceptingHandler,
                    deps: [HttpBackend, HTTP_INTERCEPTORS]
                },
                {
                    provide: ATLAS,
                    useValue: undefined
                }
            ]
        };
    }

    public static forChild(): ModuleWithProviders<LsqCommonModule> {
        console.debug('Loading libs/LsqCommonModule.forChild.');
        return {
            ngModule: LsqCommonModule,
            providers: []
        };
    }
}
